import {
  Handle,
  type Node,
  type NodeProps,
  Position,
  useEdges,
  useNodes,
} from '@xyflow/react';
import { MoreHorizontal } from 'lucide-react';
import { CSSProperties, useState, type ReactElement } from 'react';
import {
  EDGE_COLOR,
  NODE_HEIGHT,
  NODE_WIDTH,
  SUBGRAPH_EDGE_ARROW_INSET,
  SUBGRAPH_HEADER_OVERFLOW,
} from '../styles';
import { AddNodeIcon } from './AddNodeIcon';
import styles from './LBNodeDisplay.module.css';
import { useLBNodeUpdate } from './LBNodeUpdateContext';
import { getNodeRingVariant, getRunEdgeColor } from '../runVisualization';
import type { NodeRingVariant } from '../runVisualization';
import type { WorkflowRunNodeStatus } from '@/apis/logiblocksAPI';
import { LBNodeData } from '@/proto/logiblocks/logiblocks';
import {
  getLBNodeInfo,
  getNodeDisplayName,
} from '@/pages/Logiblocks/nodes/registry';
import {
  findTransferEscalationChild,
  getSubgraphRoot,
  nodeHasChild,
} from '@/pages/Logiblocks/graphTraversal';
import {
  GraphData,
  LBNodeDataWithIndexer,
  LBNodeInfo,
  OutputType,
  CATEGORY_ICON_BG,
  type ReactFlowEdge,
  type ReactFlowNode,
} from '@/pages/Logiblocks/types';
import { Dropdown } from 'antd';
import InlineInput from '@/components/InlineInput';
import CustomDeleteModal from '@/pages/Modals/CustomDeleteModal';

type LBNodeType = Node<LBNodeDataWithIndexer, 'lbNode'>;

// We add the ReactFlow-defined "nopan" CSS class to our nodes to prevent
// mouse-downs on nodes from starting a canvas pan operation. (ReactFlow
// adds this class automatically by default when `nodesDraggable` is true,
// but we set `nodesDraggable={false}`, so we have to add the class manually.)
const NOPAN_CSS_CLASS = 'nopan';

// Colour for the subgraph header's drawn-on edge to its root child. It is not
// a ReactFlow edge, so it misses the canvas-wide run colouring and has to take
// its target's status the same way. A null status map means no run is showing,
// and the edge keeps its normal colour.
function subgraphEdgeColor(
  rootId: string,
  runNodeStatuses: ReadonlyMap<string, WorkflowRunNodeStatus> | null
): string {
  if (runNodeStatuses === null) {
    return EDGE_COLOR;
  }
  return getRunEdgeColor(getNodeRingVariant(runNodeStatuses.get(rootId)));
}

// The node card's state class: validation errors take priority when no run is
// showing, otherwise the selected run's outcome.
function runStateClassName(
  hasErrors: boolean,
  runVariant: NodeRingVariant | null
): string {
  switch (runVariant) {
    case 'executed':
      return styles.nodeRunExecuted;
    case 'failed':
      return styles.nodeRunFailed;
    case 'dimmed':
      return styles.nodeRunDimmed;
    case null:
      return hasErrors ? styles.nodeError : '';
    default:
      console.warn(`Unexpected node ring variant: ${runVariant}`);
      return '';
  }
}

// Class list for the node card, used by both the regular card and the
// subgraph-parent header.
function nodeCardClassName(
  hasErrors: boolean,
  runVariant: NodeRingVariant | null,
  isClickable: boolean
): string {
  const cursorClassName = isClickable
    ? styles.nodeClickable
    : styles.nodeNotClickable;
  return `${styles.node} ${runStateClassName(hasErrors, runVariant)} ${cursorClassName} ${NOPAN_CSS_CLASS}`;
}

// We don't allow dragging edges out of handles - but ReactFlow
// requires that handles are present on nodes to render edges.
// So we make them invisible.
const invisibleHandleStyle: CSSProperties = {
  opacity: 0,
  pointerEvents: 'none',
  width: 1,
  height: 1,
};

/**
 * Renders the label for a node with two lines:
 * - Main label (larger, bold)
 * - Subtitle (smaller, gray) - only shown if getSubtitle returns a value
 */
function renderLabel(nodeData: LBNodeData, graph: GraphData): ReactElement {
  const nodeInfo = getLBNodeInfo(nodeData);
  if (!nodeInfo) return <div className={styles.labelContainer} />;
  const subtitle = nodeInfo.getSubtitle?.(nodeData, graph);
  const displayLabel = getNodeDisplayName(nodeData, nodeInfo);

  return (
    <div className={styles.labelContainer}>
      <div className={styles.labelMain}>
        <span>{displayLabel}</span>
        {subtitle && <span data-testid='lb-node-value'>: {subtitle}</span>}
      </div>
    </div>
  );
}

interface NodeContentProps {
  id: string;
  data: LBNodeDataWithIndexer;
  nodeInfo: LBNodeInfo;
  /** Already gated on run mode (always false whenever a run is showing) --
   * validation errors aren't actionable from the Runs tab. */
  hasErrors: boolean;
  /** Joined validation error messages, shown as a native tooltip on hover.
   * Undefined whenever hasErrors is false. */
  errorMessage?: string;
  /** How this node reads for the selected run, or null outside the Runs tab. */
  runVariant: NodeRingVariant | null;
  /** Whether to offer the add-child button. False on a published workflow, and
   * while a run is showing. */
  canAddChild: boolean;
  onOpenCustomizePanel?: (nodeId: string) => void;
  onAddChildClick?: (nodeId: string, rect: DOMRect) => void;
  onDeleteNode?: (nodeId: string) => void;
  graph: GraphData;
}

interface NodeOptionsMenuProps {
  onMenuClick: (info: {
    key: string;
    domEvent: React.MouseEvent | React.KeyboardEvent;
  }) => void;
  /** False for a Transfer To node's escalation mirror — it's coupled to the
   * transfer node and can only be removed by deleting that node. */
  showDelete?: boolean;
}

function NodeOptionsMenu({
  onMenuClick,
  showDelete = true,
}: NodeOptionsMenuProps) {
  return (
    <Dropdown
      menu={{
        items: [
          { key: 'rename', label: 'Rename' },
          ...(showDelete
            ? [{ key: 'delete', label: 'Delete', danger: true }]
            : []),
        ],
        onClick: onMenuClick,
      }}
      trigger={['click']}
    >
      <button
        onClick={(e) => e.stopPropagation()}
        className={styles.customizeButton}
        type='button'
        aria-label='Node options'
      >
        <MoreHorizontal color={'var(--text-secondary)'} size={16} />
      </button>
    </Dropdown>
  );
}

/**
 * Renders regular node content (non-subgraph nodes).
 */
function RegularNodeContent({
  id,
  data,
  nodeInfo,
  hasErrors,
  errorMessage,
  runVariant,
  canAddChild,
  onOpenCustomizePanel,
  onAddChildClick,
  graph,
  onDeleteNode,
}: NodeContentProps) {
  const { updateLBNodeData, runNodeStatuses, onOpenRunNodeDetail } =
    useLBNodeUpdate();
  const isRunMode = runNodeStatuses !== null;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const handleCustomizeClick = () => {
    if (isRunMode) {
      onOpenRunNodeDetail?.(id);
      return;
    }
    if (onOpenCustomizePanel) {
      onOpenCustomizePanel(id);
    }
  };

  const isCardClickable = isRunMode
    ? onOpenRunNodeDetail !== undefined
    : nodeInfo?.CustomizationComponent !== undefined;

  // Coupled to its Transfer To node — can't be deleted independently.
  const isEscalationMirror =
    data.ai_call_action?.is_transfer_escalation === true;

  const handleMenuClick = ({
    key,
    domEvent,
  }: {
    key: string;
    domEvent: React.MouseEvent | React.KeyboardEvent;
  }) => {
    domEvent.stopPropagation();
    if (key === 'delete' && !isEscalationMirror) {
      setShowDeleteModal(true);
    } else if (key === 'rename') {
      setRenameValue(getNodeDisplayName(data, nodeInfo));
      setIsRenaming(true);
    }
  };

  const handleRenameSave = () => {
    if (renameValue.trim()) {
      updateLBNodeData(id, { custom_display_name: renameValue.trim() });
    }
    setIsRenaming(false);
  };

  const handleRenameCancel = () => {
    setIsRenaming(false);
  };

  const hasChild = nodeHasChild(id, graph.edges);

  return (
    <>
      <div
        data-testid='lb-node-card'
        className={nodeCardClassName(hasErrors, runVariant, isCardClickable)}
        title={errorMessage}
        // eslint-disable-next-line react/forbid-dom-props -- `graphLayout` positions nodes from these same sizes.
        style={{ width: NODE_WIDTH, minHeight: NODE_HEIGHT }}
        onClick={isCardClickable ? handleCustomizeClick : undefined}
      >
        {/* Invisible target handle for edge anchoring (only if hasInput) */}
        {nodeInfo?.hasInput && (
          <Handle
            type='target'
            position={Position.Top}
            style={invisibleHandleStyle}
          />
        )}

        {isRenaming ? (
          <div
            className={styles.labelContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <InlineInput
              value={renameValue}
              placeholder='Enter title...'
              onChange={setRenameValue}
              onAdd={handleRenameSave}
              onCancel={handleRenameCancel}
              autoFocus
            />
          </div>
        ) : (
          <>
            <div
              className={'lb-icon-wrapper'}
              // eslint-disable-next-line react/forbid-dom-props -- The color varies by category, and other panels read CATEGORY_ICON_BG too.
              style={{ backgroundColor: CATEGORY_ICON_BG[nodeInfo?.category] }}
            >
              <div className={styles.icon}>{nodeInfo?.getIcon(16)}</div>
            </div>
            {renderLabel(data, graph)}

            {/* Options menu button - shown when LBNode supports customization */}
            {nodeInfo?.CustomizationComponent && (
              <NodeOptionsMenu
                onMenuClick={handleMenuClick}
                showDelete={!isEscalationMirror}
              />
            )}
          </>
        )}

        {/* Invisible source handle for edge anchoring */}
        <Handle
          type='source'
          position={Position.Bottom}
          style={invisibleHandleStyle}
        />

        {/* Node extension: vertical line with plus button. canAddChild already
          folds in read-only, run mode, and (for a Transfer To node) whether its
          escalation mirror is already materialized — nothing can be inserted
          between them. When the node already has a child, the line is
          suppressed because the real graph edge to that child overlaps the
          same space. */}
        {canAddChild && (
          <div
            className={`${styles.nodeExtension} ${styles.nodeExtensionBelowNode}`}
          >
            <div
              className={
                hasChild
                  ? `${styles.extensionLine} ${styles.extensionLineHidden}`
                  : styles.extensionLine
              }
            />
            <button
              className={styles.plusButton}
              type='button'
              aria-label='Add child node'
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                onAddChildClick?.(id, rect);
              }}
            >
              <AddNodeIcon />
            </button>
          </div>
        )}
      </div>
      <CustomDeleteModal
        visible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          setShowDeleteModal(false);
          onDeleteNode?.(id);
        }}
        title='Delete node?'
        message='Are you sure you want to delete this node? This node and all its child nodes will also be deleted.'
        cancelText='Cancel'
        confirmText='Delete'
        confirmColor='var(--text-primary)'
      />
    </>
  );
}

interface SubgraphParentNodeContentProps extends NodeContentProps {
  width: number;
  height: number;
}

/**
 * Renders subgraph parent node content (e.g., FilteredShipmentsLoop).
 * Displays as a group container with header above and dashed border.
 * The header looks exactly like a RegularNode.
 */
function SubgraphParentNodeContent({
  id,
  data,
  nodeInfo,
  hasErrors,
  errorMessage,
  runVariant,
  canAddChild,
  onOpenCustomizePanel,
  onAddChildClick,
  graph,
  width,
  height,
  onDeleteNode,
}: SubgraphParentNodeContentProps) {
  const { updateLBNodeData, runNodeStatuses, onOpenRunNodeDetail } =
    useLBNodeUpdate();
  const isRunMode = runNodeStatuses !== null;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const handleCustomizeClick = () => {
    if (isRunMode) {
      onOpenRunNodeDetail?.(id);
      return;
    }
    if (onOpenCustomizePanel) {
      onOpenCustomizePanel(id);
    }
  };

  const isCardClickable = isRunMode
    ? onOpenRunNodeDetail !== undefined
    : nodeInfo?.CustomizationComponent !== undefined;

  const handleMenuClick = ({
    key,
    domEvent,
  }: {
    key: string;
    domEvent: React.MouseEvent | React.KeyboardEvent;
  }) => {
    domEvent.stopPropagation();
    if (key === 'delete') {
      setShowDeleteModal(true);
    } else if (key === 'rename') {
      setRenameValue(getNodeDisplayName(data, nodeInfo));
      setIsRenaming(true);
    }
  };

  const handleRenameSave = () => {
    if (renameValue.trim()) {
      updateLBNodeData(id, { custom_display_name: renameValue.trim() });
    }
    setIsRenaming(false);
  };

  const handleRenameCancel = () => {
    setIsRenaming(false);
  };

  // Find the root node inside this subgraph (if any)
  const subgraphRoot = getSubgraphRoot(id, graph.nodes, graph.edges);
  const rootEdgeColor = subgraphRoot
    ? subgraphEdgeColor(subgraphRoot.id, runNodeStatuses)
    : EDGE_COLOR;

  return (
    <>
      <div
        className={styles.subgraphContainer}
        // eslint-disable-next-line react/forbid-dom-props -- `graphLayout` computes a subgraph's size from its contents.
        style={{ width, height }}
      >
        {/* Header - styled exactly like RegularNodeContent */}
        <div
          data-testid='lb-node-card'
          className={`${nodeCardClassName(
            hasErrors,
            runVariant,
            isCardClickable
          )} ${styles.subgraphHeader}`}
          title={errorMessage}
          // eslint-disable-next-line react/forbid-dom-props -- `graphLayout` positions nodes from these same sizes and this same overflow.
          style={{
            top: -SUBGRAPH_HEADER_OVERFLOW,
            width: NODE_WIDTH,
            minHeight: NODE_HEIGHT,
          }}
          onClick={isCardClickable ? handleCustomizeClick : undefined}
        >
          {nodeInfo?.hasInput && (
            <Handle
              type='target'
              position={Position.Top}
              style={invisibleHandleStyle}
            />
          )}

          {isRenaming ? (
            <div
              className={styles.labelContainer}
              onClick={(e) => e.stopPropagation()}
            >
              <InlineInput
                value={renameValue}
                placeholder='Enter title...'
                onChange={setRenameValue}
                onAdd={handleRenameSave}
                onCancel={handleRenameCancel}
                autoFocus
              />
            </div>
          ) : (
            <>
              <div
                className={'lb-icon-wrapper'}
                // eslint-disable-next-line react/forbid-dom-props -- The color varies by category, and other panels read CATEGORY_ICON_BG too.
                style={{
                  backgroundColor: CATEGORY_ICON_BG[nodeInfo?.category],
                }}
              >
                <div className={styles.icon}>{nodeInfo?.getIcon(16)}</div>
              </div>
              {renderLabel(data, graph)}
              {nodeInfo?.CustomizationComponent && (
                <NodeOptionsMenu onMenuClick={handleMenuClick} />
              )}
            </>
          )}
        </div>

        {/* Fake graph edge from header to subgraph root (when not empty) */}
        {subgraphRoot && (
          <svg className={styles.subgraphEdgeOverlay}>
            <defs>
              <marker
                id={`subgraph-arrow-${id}`}
                markerWidth='12.5'
                markerHeight='12.5'
                viewBox='-10 -10 20 20'
                markerUnits='strokeWidth'
                orient='auto-start-reverse'
                refX='0'
                refY='0'
              >
                <polyline
                  stroke={rootEdgeColor}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='1'
                  fill={rootEdgeColor}
                  points='-5,-4 0,0 -5,4 -5,-4'
                />
              </marker>
            </defs>
            <line
              x1={width / 2}
              y1={NODE_HEIGHT}
              x2={subgraphRoot.position.x}
              y2={subgraphRoot.position.y - SUBGRAPH_EDGE_ARROW_INSET}
              stroke={rootEdgeColor}
              strokeWidth={1.5}
              markerEnd={`url(#subgraph-arrow-${id})`}
            />
          </svg>
        )}

        {/* Plus button inside the subgraph container */}
        {canAddChild && (
          <div
            className={`${styles.nodeExtension} ${styles.nodeExtensionInSubgraph}`}
            // eslint-disable-next-line react/forbid-dom-props -- `graphLayout` positions the subgraph's contents from this same height and overflow.
            style={{ top: NODE_HEIGHT - SUBGRAPH_HEADER_OVERFLOW }}
          >
            <div className={styles.extensionLine} />
            <button
              className={styles.plusButton}
              type='button'
              aria-label='Add child node'
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                onAddChildClick?.(id, rect);
              }}
            >
              <AddNodeIcon />
            </button>
          </div>
        )}

        {/* Dashed outline box for subgraph container */}
        <div className={styles.subgraphOutline} />

        <Handle
          type='source'
          position={Position.Bottom}
          style={invisibleHandleStyle}
        />
      </div>
      <CustomDeleteModal
        visible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          setShowDeleteModal(false);
          onDeleteNode?.(id);
        }}
        title='Delete node?'
        message='Are you sure you want to delete this node? This node and all its child nodes will also be deleted.'
        cancelText='Cancel'
        confirmText='Delete'
        confirmColor='var(--text-primary)'
      />
    </>
  );
}

/**
 * Custom React Flow node component for Logiblocks workflow nodes.
 *
 * Registered in LBCanvas via `nodeTypes={{ lbNode: LBNode }}`. React Flow
 * renders this component for each node object with `type: 'lbNode'`.
 */
export function LBNodeDisplay({ data, id }: NodeProps<LBNodeType>) {
  const {
    onOpenCustomizePanel,
    onAddChildClick,
    onDeleteNode,
    isReadOnly,
    validationErrors,
    runNodeStatuses,
  } = useLBNodeUpdate();
  const nodes = useNodes<ReactFlowNode>();
  const edges = useEdges<ReactFlowEdge>();

  const nodeInfo = getLBNodeInfo(data);
  // A run is showing, so the card reports its outcome. Validation errors are
  // not actionable from the Runs tab and would paint the same red as a failure.
  const isRunMode = runNodeStatuses !== null;
  const runVariant = isRunMode
    ? getNodeRingVariant(runNodeStatuses.get(id))
    : null;
  const nodeErrorsRaw = validationErrors.get(id);
  // A brand-new, never-configured node (still exactly its type's default
  // data) is always invalid - empty required fields - the instant it's
  // created. Painting it red before the author has touched it would be a
  // false alarm, not a real one. Filtered out one level up (LBCanvas's
  // presentedValidationErrors), so validationErrors here already excludes
  // it - the underlying raw map (and the backend's publish-time check) are
  // untouched, so an unconfigured node still can't actually be published.
  const hasErrors =
    !isRunMode && nodeErrorsRaw !== undefined && nodeErrorsRaw.length > 0;
  const errorMessage = hasErrors
    ? nodeErrorsRaw!.map((e) => e.message).join('\n')
    : undefined;
  const graph = { nodes, edges };
  // A Transfer To node's escalation mirror is a read-only display copy of its
  // own agent selection — no node can be inserted between them.
  const hasEscalationMirrorChild =
    data.transfer_to_action != null &&
    findTransferEscalationChild(id, graph.nodes, graph.edges) != null;
  const canAddChild =
    !isReadOnly &&
    !isRunMode &&
    !hasEscalationMirrorChild &&
    !nodeInfo?.endsBranch;

  if (!nodeInfo) return null;

  const isSubgraphParent = nodeInfo?.outputType === OutputType.SUBGRAPH_PARENT;

  if (isSubgraphParent) {
    // Layout always writes width/height on SUBGRAPH_PARENT nodes.
    const width = data.width as number;
    const height = data.height as number;

    return (
      <SubgraphParentNodeContent
        id={id}
        data={data}
        nodeInfo={nodeInfo}
        hasErrors={hasErrors}
        errorMessage={errorMessage}
        runVariant={runVariant}
        canAddChild={canAddChild}
        onOpenCustomizePanel={onOpenCustomizePanel}
        onAddChildClick={onAddChildClick}
        onDeleteNode={onDeleteNode}
        graph={graph}
        width={width}
        height={height}
      />
    );
  }

  return (
    <RegularNodeContent
      id={id}
      data={data}
      nodeInfo={nodeInfo}
      hasErrors={hasErrors}
      errorMessage={errorMessage}
      runVariant={runVariant}
      canAddChild={canAddChild}
      onOpenCustomizePanel={onOpenCustomizePanel}
      onAddChildClick={onAddChildClick}
      onDeleteNode={onDeleteNode}
      graph={graph}
    />
  );
}
